import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { CurrentUserResponse } from '../types/api';
import { apiService } from '../services/api';

interface UserContextType {
  user: CurrentUserResponse | null;
  avatarInitials: string;
  accessToken: string | null;
  isLoading: boolean;
  error: string | null;
  /** `background` re-reads without raising `isLoading`, so PermissionGate keeps the page mounted. */
  refreshUser: (options?: { background?: boolean }) => Promise<void>;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

interface UserProviderProps {
  children: React.ReactNode;
}

const getInitialsFromUser = (user: CurrentUserResponse | null): string => {
  if (!user) {
    return 'U';
  }

  const first = user.firstName?.trim();
  const last = user.lastName?.trim();

  if (first && last) {
    return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();
  }
  if (first) {
    return first.slice(0, 2).toUpperCase();
  }
  if (last) {
    return last.slice(0, 2).toUpperCase();
  }

  const email = user.email?.trim();
  if (email) {
    const local = email.split('@')[0] ?? '';
    const segments = local.split(/[^a-zA-Z0-9]+/).filter(Boolean);
    if (segments.length >= 2) {
      return `${segments[0].charAt(0)}${segments[segments.length - 1].charAt(0)}`.toUpperCase();
    }
    if (local.length >= 2) {
      return local.slice(0, 2).toUpperCase();
    }
    if (local.length === 1) {
      return local.toUpperCase();
    }
  }

  return 'U';
};

/**
 * How stale the cached profile has to be before returning to the tab refetches
 * it. Memberships and role grants change rarely, so this exists only to stop a
 * burst of tab flicking from firing a request each time — it is not a poll.
 */
const REFETCH_ON_VISIBLE_MIN_INTERVAL_MS = 30_000;

export const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
  const auth = useAuth();
  const accessToken = auth.user?.access_token ?? null;
  const [user, setUser] = useState<CurrentUserResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const lastLoadedAtRef = useRef(0);

  const loadCurrentUser = useCallback(async (options?: { background?: boolean }) => {
    if (!accessToken) {
      setUser(null);
      setError(null);
      setIsLoading(false);
      return;
    }

    // A background refresh must not raise `isLoading`: PermissionGate reads it
    // through usePermissions and replaces the whole page while it is true, so
    // doing so would blink every page to a skeleton on returning to the tab.
    const background = options?.background === true;

    try {
      if (!background) {
        setIsLoading(true);
        setError(null);
      }
      const response = await apiService.getCurrentUser(accessToken);
      setUser(response);
      setError(null);
      lastLoadedAtRef.current = Date.now();
    } catch (err: unknown) {
      console.error('Failed to load current user:', err);
      // A background refresh that fails leaves the profile it already has: the
      // user is working, the token has not changed, and a transient network
      // error is not evidence their access was revoked. A real revocation
      // still lands, because the silent token renewal fails and signs them out.
      if (!background) {
        // Stored raw on purpose: this string becomes OrganizationContext's
        // membershipError and reaches the screen only through the shell banner's
        // ErrorState, which humanizes it. Humanizing here would humanize twice, and the second
        // pass matches no pattern — every message would degrade to the generic.
        setError(err instanceof Error ? err.message : 'Failed to load current user'); // design-system-allow: humanized by every render site
        setUser(null);
      }
    } finally {
      if (!background) {
        setIsLoading(false);
      }
    }
  }, [accessToken]);

  useEffect(() => {
    if (!accessToken) {
      setUser(null);
      setError(null);
      setIsLoading(false);
      return;
    }

    loadCurrentUser();
  }, [accessToken, loadCurrentUser]);

  // The profile — memberships and the permissions the sidebar and the route
  // gates are built from — is otherwise fetched once per access token. An
  // administrator changing someone's roles fires no sign-out, so that user's
  // menu keeps offering pages they can no longer open until the token rotates,
  // roughly an hour with silent renew on.
  //
  // Refetching when the tab becomes visible again closes that window at the
  // moment it matters: coming back to the page is exactly when a stale menu
  // gets clicked. `visibilitychange` is the narrow signal — unlike `focus` it
  // does not fire for every click back into the window from another
  // application — and deliberately not a timer: nothing here changes often
  // enough to justify polling every open tab.
  useEffect(() => {
    if (!accessToken) return undefined;

    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return;
      if (Date.now() - lastLoadedAtRef.current < REFETCH_ON_VISIBLE_MIN_INTERVAL_MS) return;
      void loadCurrentUser({ background: true });
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [accessToken, loadCurrentUser]);

  const value = useMemo<UserContextType>(
    () => ({
      user,
      avatarInitials: getInitialsFromUser(user),
      accessToken,
      isLoading,
      error,
      refreshUser: loadCurrentUser,
    }),
    [accessToken, error, isLoading, loadCurrentUser, user],
  );

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

export const useUser = (): UserContextType => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};
